import Banner from '../Banner';
import { useChartContext } from './ChartContext';
import * as BannerUI from '../../components/Banner/BannerUI';
import { isNil } from 'lodash';
import { appRoutes } from '../../path';
import { UnreachableCaseError } from 'ts-essentials';
import styled from 'styled-components';
import * as ExecutionHistoryUI from '../ExecutionHistory/ExecutionHistoryUI';
import { Link } from 'react-router-dom';
import useCachedEntitiesSelector from '../../redux/selectorHooks/useCachedEntitiesSelector';
import { theme } from '../../styles/theme';

const StyledBanner = styled(Banner)`
  margin: 16px 0;
  padding: 4px 16px;
  border-radius: 4px;
  font-size: 12px;
  min-height: 36px;
`;

const ChartBreadcrumbs = styled.div`
  color: ${theme.colors.secondary600};
  margin: 16px 0;
  display: flex;
  gap: 4px;
`;

const StyledLink = styled(Link)`
  color: inherit;
  font-size: 12px;
  font-style: normal;
  font-weight: 400;

  &:hover {
    color: ${theme.colors.mainBlue};
  }
`;

const ChartBuilderBanner = () => {
  const {
    metadata: { container, display: chartTitle, reportLink },
  } = useChartContext();
  const replaceReport = useCachedEntitiesSelector('replaceReport');

  if (!isNil(replaceReport)) {
    let containerLink: string;
    if (replaceReport.type === 'dashboard') {
      containerLink = appRoutes.dashboard.generateLink(replaceReport.baseId);
    } else if (replaceReport.type === 'workspace') {
      containerLink = appRoutes.workspaces.workspace(replaceReport.workspaceId);
    } else {
      throw new UnreachableCaseError(replaceReport);
    }
    return (
      <ChartBreadcrumbs>
        <StyledLink to={containerLink}>{replaceReport.name}</StyledLink> /{' '}
        <StyledLink to={replaceReport.targetAnalysisLink}>
          {replaceReport.targetAnalysisName}
        </StyledLink>
      </ChartBreadcrumbs>
    );
  } else if (!isNil(container)) {
    if (container.__typename === 'AnalysisDashboardContainer') {
      let containerLink: string;
      if (container.isLatestInstance) {
        containerLink = appRoutes.dashboard.generateLink(container.baseId);
      } else {
        containerLink = appRoutes.dashboard.generateLink(
          container.baseId,
          container.id,
        );
      }
      if (container.isLatestInstance) {
        return (
          <ChartBreadcrumbs>
            <StyledLink to={containerLink}>{container.name}</StyledLink> /{' '}
            <StyledLink to={reportLink}>{chartTitle}</StyledLink>
          </ChartBreadcrumbs>
        );
      } else if (!container.isDefault) {
        return (
          <StyledBanner
            variant="info"
            rightSideComponent={
              <BannerUI.ButtonLink
                forwardedAs={Link}
                to={containerLink}
                isOutlined={true}
              >
                Go to dashboard
              </BannerUI.ButtonLink>
            }
          >
            <span title={container.name}>
              Unable to save changes, Report belongs to a filtered dashboard
            </span>
          </StyledBanner>
        );
      } else {
        return (
          <StyledBanner
            variant="info"
            rightSideComponent={
              <BannerUI.ButtonLink
                forwardedAs={Link}
                to={containerLink}
                isOutlined={true}
              >
                Go to dashboard
              </BannerUI.ButtonLink>
            }
          >
            <span title={container.name}>
              Unable to save changes, Report belongs to a historical dashboard
            </span>
          </StyledBanner>
        );
      }
    } else if (container.__typename === 'AnalysisWorkspaceContainer') {
      const containerLink = appRoutes.workspaces.workspace(container.baseId);
      return (
        <ChartBreadcrumbs>
          <StyledLink to={containerLink}>{container.name}</StyledLink> /{' '}
          <StyledLink to={reportLink}>{chartTitle}</StyledLink>
        </ChartBreadcrumbs>
      );
    } else {
      throw new UnreachableCaseError(container);
    }
  } else {
    return <ExecutionHistoryUI.ExecutionSplitLine />;
  }
};

export default ChartBuilderBanner;
